import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Loader2, ReceiptText, CheckCircle2, CalendarClock, Repeat, AlertTriangle, Clock, SkipForward, X, ArrowDownLeft, Car, HandCoins, ChevronRight, Maximize2, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DatePickerHR } from "../components/DatePickerHR";
import api, { fmtNum, fmtDate } from "../lib/api";

const TODAY = new Date().toISOString().slice(0, 10);
const CONF_LABEL = { high: "visoka", medium: "srednja", low: "niska" };

const STATUS_BADGE = {
  "čeka": { cls: "bg-[#F1F5F9] text-[#475569]", icon: Clock, label: "Čeka" },
  "čita": { cls: "bg-[#0D9488]/10 text-[#0D9488]", icon: Loader2, label: "AI čita..." },
  "spremno": { cls: "bg-[#D97706]/15 text-[#D97706]", icon: ReceiptText, label: "Za pregled" },
  "spremljeno": { cls: "bg-[#2F7D4F]/15 text-[#2F7D4F]", icon: CheckCircle2, label: "Spremljeno" },
  "preskočeno": { cls: "bg-[#F1F5F9] text-[#64748B]", icon: SkipForward, label: "Preskočeno" },
  "greška": { cls: "bg-[#DC2626]/15 text-[#DC2626]", icon: AlertTriangle, label: "Greška" },
};

export default function InvoicesPage() {
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [queue, setQueue] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dupWarn, setDupWarn] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [recv, setRecv] = useState(null);
  const [expandOpen, setExpandOpen] = useState(false);
  const fileRef = useRef();
  const cameraRef = useRef();
  const banksRef = useRef([]);
  const queueRef = useRef([]);
  const activeIdRef = useRef(null);
  const processingRef = useRef(false);

  useEffect(() => {
    api.get("/banks").then((r) => { setBanks(r.data); banksRef.current = r.data; });
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/receivables").then((r) => setRecv(r.data)).catch(() => {});
    api.get("/invoices/pending").then((r) => {
      const existing = new Set(queueRef.current.map((q) => q.pending_id).filter(Boolean));
      const items = (r.data.items || []).filter((d) => !existing.has(d.id)).map((d) => ({
        id: `p-${d.id}`, pending_id: d.id, name: d.filename || d.counterparty || "račun",
        file: null, preview: null, status: "spremno", data: d, error: "",
      }));
      if (items.length) {
        queueRef.current = [...items, ...queueRef.current];
        syncQueue();
        if (!activeIdRef.current) activateNext();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (banks.length) setForm((f) => (f && !f.bank_id ? { ...f, bank_id: banks[0].id } : f));
  }, [banks, form]);

  const syncQueue = () => setQueue([...queueRef.current]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openExternal = (src) => {
    if (src && src.startsWith("data:")) {
      fetch(src).then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"));
    } else if (src) window.open(src, "_blank");
  };

  useEffect(() => {
    if (!form) { setDupWarn(null); return; }
    const amount = parseFloat(String(form.amount).replace(",", "."));
    const t = setTimeout(() => {
      api.get("/invoices/duplicate-check", { params: {
        invoice_number: form.invoice_number || "",
        counterparty: form.counterparty || "",
        amount: isNaN(amount) ? 0 : amount,
        currency: form.currency || "",
      }}).then((r) => setDupWarn(r.data.count > 0 ? r.data : null)).catch(() => setDupWarn(null));
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.invoice_number, form?.amount, form?.counterparty, form?.currency, activeId]);

  const openItem = (item) => {
    activeIdRef.current = item.id;
    setActiveId(item.id);
    setPreview(item.preview || item.data?.preview_data || null);
    setForm({
      counterparty: item.data.counterparty,
      amount: item.data.amount ? String(item.data.amount) : "",
      currency: item.data.currency,
      due_date: item.data.due_date || "",
      invoice_number: item.data.invoice_number,
      description: item.data.description || "",
      category: item.data.category,
      type: item.data.direction === "izlazni" ? "naplata" : "placanje",
      bank_id: item.data.bank_id || banksRef.current[0]?.id || "",
      vehicle: item.data.vehicle || "",
      direction: item.data.direction || "ulazni",
      bank_name: item.data.bank_name || "",
      account_number: item.data.account_number || "",
      detected_plate: item.data.detected_plate || "",
      confidence: item.data.confidence,
      triage: item.data.triage || null,
    });
  };

  const activateNext = () => {
    const next = queueRef.current.find((q) => q.status === "spremno");
    if (next) {
      openItem(next);
    } else {
      activeIdRef.current = null;
      setActiveId(null);
      setForm(null);
      setPreview(null);
    }
  };

  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    let item;
    while ((item = queueRef.current.find((q) => q.status === "čeka"))) {
      item.status = "čita";
      syncQueue();
      const fd = new FormData();
      fd.append("file", item.file);
      try {
        const r = await api.post("/invoices/parse", fd, { timeout: 120000 });
        item.data = r.data;
        item.pending_id = r.data.pending_id;
        item.status = "spremno";
      } catch (err) {
        item.status = "greška";
        item.error = err.response?.data?.detail || "Greška pri čitanju";
      }
      syncQueue();
      if (!activeIdRef.current) activateNext();
    }
    processingRef.current = false;
  };

  const addFiles = (fileList, inputEl) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      queueRef.current.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: f.name || "slika.jpg",
        file: f,
        fileUrl: URL.createObjectURL(f),
        isPdf: f.type === "application/pdf" || (f.name || "").toLowerCase().endsWith(".pdf"),
        preview: f.type?.startsWith("image/") ? URL.createObjectURL(f) : null,
        status: "čeka",
        data: null,
        error: "",
      });
    }
    syncQueue();
    if (files.length > 1) toast.info(`${files.length} računa dodano u red — AI ih čita jedan po jedan`);
    if (inputEl) inputEl.value = "";
    processQueue();
  };

  const markActive = (status, label) => {
    const cur = queueRef.current.find((q) => q.id === activeIdRef.current);
    if (cur) {
      cur.status = status;
      if (label) cur.savedAs = label;
      if (cur.pending_id) api.delete(`/invoices/pending/${cur.pending_id}`).catch(() => {});
      syncQueue();
    }
    activateNext();
  };

  const clearQueue = () => {
    queueRef.current = queueRef.current.filter((q) => q.status === "čeka" || q.status === "čita" || q.status === "spremno");
    syncQueue();
  };

  const validate = () => {
    const amount = parseFloat(String(form.amount).replace(",", "."));
    if (!form.counterparty.trim()) { toast.error("Unesite primatelja"); return null; }
    if (isNaN(amount) || amount <= 0) { toast.error("Unesite ispravan iznos"); return null; }
    if (!form.bank_id) { toast.error("Odaberite banku"); return null; }
    return amount;
  };

  const saveTx = async (status) => {
    const amount = validate();
    if (amount == null) return;
    if (status === "planirano" && !form.due_date) { toast.error("Za planirano unesite datum dospijeća"); return; }
    setSaving(true);
    try {
      await api.post("/transactions", {
        date: status === "planirano" ? form.due_date : (form.due_date || TODAY),
        bank_id: form.bank_id,
        type: form.type,
        currency: form.currency,
        amount,
        counterparty: form.counterparty.trim(),
        recipient_bank: form.direction === "ulazni" ? (form.bank_name || "") : "",
        recipient_account: form.direction === "ulazni" ? (form.account_number || "") : "",
        category: form.category,
        note: [form.invoice_number ? `Račun br. ${form.invoice_number}` : "", (form.description || "").trim()].filter(Boolean).join(" — ") || "Uvezeno slikanjem računa",
        status,
        vehicle: form.vehicle || "",
        vehicle_id: vehicles.find((v) => v.plate === form.vehicle)?.id || "",
      });
      const what = form.type === "naplata" ? "planirana naplata" : "planirani trošak";
      toast.success(status === "planirano" ? `Spremljeno: ${what} (dospijeće ${form.due_date})` : "Spremljeno kao ostvareno");
      api.get("/receivables").then((r) => setRecv(r.data)).catch(() => {});
      markActive("spremljeno", status);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spremanju");
    } finally {
      setSaving(false);
    }
  };

  const saveRecurring = async () => {
    const amount = validate();
    if (amount == null) return;
    if (!form.due_date) { toast.error("Za fiksnu stavku unesite datum dospijeća"); return; }
    setSaving(true);
    try {
      await api.post("/recurring", {
        name: form.counterparty.trim(),
        bank_id: form.bank_id,
        type: form.type,
        currency: form.currency,
        amount,
        category: form.category,
        day_of_month: parseInt(form.due_date.slice(8), 10),
        start_month: form.due_date.slice(0, 7),
        vehicle: form.vehicle || "",
        vehicle_id: vehicles.find((v) => v.plate === form.vehicle)?.id || "",
      });
      toast.success("Spremljeno kao fiksna mjesečna stavka — na stranici Fiksni troškovi/prihodi prebacite je u planirane transakcije");
      markActive("spremljeno", "fiksno");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spremanju");
    } finally {
      setSaving(false);
    }
  };

  const parsing = queue.some((q) => q.status === "čeka" || q.status === "čita");
  const pendingReview = queue.filter((q) => q.status === "spremno").length;
  const doneCount = queue.filter((q) => q.status === "spremljeno" || q.status === "preskočeno").length;
  const activeItem = queue.find((q) => q.id === activeId);
  const pdfUrl = activeItem?.isPdf && activeItem.fileUrl ? activeItem.fileUrl : null;

  return (
    <div className="space-y-6 fade-up" data-testid="invoices-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">AI čitanje računa</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Računi</h1>
        <p className="text-sm text-[#475569] mt-2">Slikajte račun ili učitajte više slika/PDF-ova odjednom — AI ih čita redom, a vi svaki pregledate i spremite kao planirano, ostvareno ili fiksnu stavku.</p>
      </div>

      {recv?.count > 0 && (
        <Link to="/nenaplaceno" data-testid="invoices-receivables-bar" className={`flex items-center gap-3 bg-white border rounded-sm px-4 py-3 text-sm hover:bg-[#F8FAFC] transition-colors duration-200 ${recv.overdue_count > 0 ? "border-[#DC2626]/40" : "border-[#E2E8F0]"}`}>
          <span className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${recv.overdue_count > 0 ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-[#0D9488]/10 text-[#0D9488]"}`}>
            <HandCoins className="w-4 h-4" />
          </span>
          <span className="min-w-0 text-[#0F172A]">
            <span className="font-semibold">{recv.count} {recv.count === 1 ? "izlazni račun čeka" : "izlaznih računa čeka"} naplatu</span>
            {" · ukupno "}<span className="font-semibold tabular-nums">{fmtNum(recv.total_eur, "€")}</span>
            {recv.overdue_count > 0 && <span className="text-[#DC2626] font-semibold"> · {recv.overdue_count} kasni</span>}
          </span>
          <ChevronRight className="w-4 h-4 text-[#CBD5E1] ml-auto shrink-0" />
        </Link>
      )}

      <div className="flex flex-wrap gap-3">
        <input ref={cameraRef} data-testid="invoice-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => addFiles(e.target.files, e.target)} />
        <input ref={fileRef} data-testid="invoice-file-input" type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(e) => addFiles(e.target.files, e.target)} />
        <Button data-testid="invoice-camera-btn" onClick={() => cameraRef.current?.click()} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
          <Camera className="w-4 h-4 mr-2" /> Slikaj račun
        </Button>
        <Button data-testid="invoice-upload-btn" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm border-[#CBD5E1]">
          <ReceiptText className="w-4 h-4 mr-2" /> Učitaj slike ili PDF (više odjednom)
        </Button>
        {parsing && (
          <span className="flex items-center gap-2 text-sm text-[#475569]" data-testid="invoice-parsing-indicator"><Loader2 className="w-4 h-4 animate-spin text-[#0D9488]" /> AI čita račune...</span>
        )}
      </div>

      {queue.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid="invoice-queue">
          <div className="px-4 py-2.5 bg-[#F1F5F9] flex items-center justify-between">
            <p className="text-sm font-semibold text-[#0F172A]">Red obrade — {doneCount}/{queue.length} obrađeno{pendingReview > 0 ? `, ${pendingReview} čeka pregled` : ""}</p>
            {doneCount > 0 && (
              <button data-testid="invoice-queue-clear" onClick={clearQueue} className="text-xs text-[#64748B] hover:text-[#DC2626] flex items-center gap-1"><X className="w-3 h-3" /> Makni obrađene</button>
            )}
          </div>
          <div className="divide-y divide-[#F1F5F9] max-h-56 overflow-y-auto">
            {queue.map((q, i) => {
              const sb = STATUS_BADGE[q.status];
              const Icon = sb.icon;
              const clickable = q.status === "spremno" && q.id !== activeId;
              return (
                <div
                  key={q.id}
                  data-testid={`invoice-queue-item-${i}`}
                  onClick={() => clickable && openItem(q)}
                  className={`px-4 py-2 flex items-center gap-3 text-sm ${q.id === activeId ? "bg-[#0D9488]/5 border-l-2 border-l-[#0D9488]" : ""} ${clickable ? "cursor-pointer hover:bg-[#F8FAFC]" : ""}`}
                >
                  <span className="text-xs text-[#64748B] tabular-nums w-6">{i + 1}.</span>
                  <span className="truncate flex-1 text-[#0F172A]">{q.name}</span>
                  {q.data?.counterparty && <span className="hidden sm:block text-xs text-[#475569] truncate max-w-[200px]">{q.data.counterparty} · {q.data.amount} {q.data.currency}</span>}
                  {q.status === "greška" && <span className="text-xs text-[#DC2626] truncate max-w-[220px]">{q.error}</span>}
                  <Badge className={`rounded-sm text-[11px] gap-1 hover:bg-inherit shrink-0 ${sb.cls}`}>
                    <Icon className={`w-3 h-3 ${q.status === "čita" ? "animate-spin" : ""}`} /> {sb.label}{q.savedAs ? ` (${q.savedAs})` : ""}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {form && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-sm p-5 space-y-4" data-testid="invoice-review-form">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Provjerite pročitane podatke</h2>
              <div className="flex items-center gap-2">
                <Badge className={`rounded-sm text-xs ${form.confidence === "high" ? "bg-[#2F7D4F]/15 text-[#2F7D4F]" : form.confidence === "low" ? "bg-[#DC2626]/15 text-[#DC2626]" : "bg-[#D97706]/15 text-[#D97706]"} hover:bg-inherit`} data-testid="invoice-confidence">
                  AI sigurnost: {CONF_LABEL[form.confidence]}
                </Badge>
                <Button data-testid="invoice-skip-btn" size="sm" variant="ghost" onClick={() => markActive("preskočeno")} className="h-7 text-xs text-[#64748B] hover:text-[#DC2626] rounded-sm">
                  <SkipForward className="w-3.5 h-3.5 mr-1" /> Preskoči
                </Button>
              </div>
            </div>
            {form.triage?.warning && (
              <div data-testid="invoice-triage-warning" className="flex items-center gap-2 bg-[#D97706]/10 border border-[#D97706]/40 rounded-sm px-3 py-2 text-sm text-[#92400E] font-medium">
                <AlertTriangle className="w-4 h-4 shrink-0 text-[#D97706]" />
                {form.triage.warning}
              </div>
            )}
            {form.direction === "izlazni" && (
              <div data-testid="invoice-direction-badge" className="flex items-center gap-2 bg-[#0D9488]/5 border border-[#0D9488]/25 rounded-sm px-3 py-2 text-sm text-[#0D9488] font-medium">
                <ArrowDownLeft className="w-4 h-4 shrink-0" />
                Izlazni račun (izdaje VTZ) — očekuje se NAPLATA od kupca{form.bank_name ? ` · uplata na: ${form.bank_name}` : ""}
              </div>
            )}
            {dupWarn && (
              <div data-testid="invoice-dup-warning" className="bg-[#D97706]/10 border border-[#D97706]/40 rounded-sm px-4 py-3 space-y-1.5">
                <p className="text-sm font-semibold text-[#92400E] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-[#D97706]" /> Mogući dupli račun — već postoji u transakcijama:
                </p>
                {dupWarn.matches.map((m, i) => (
                  <p key={m.id} data-testid={`invoice-dup-match-${i}`} className="text-xs text-[#0F172A] pl-6">
                    {fmtDate(m.date)} · {m.counterparty || "—"} · <span className="font-semibold tabular-nums">{fmtNum(m.amount, m.currency === "EUR" ? "€" : "KM")}</span> · {m.status === "planirano" ? "planirano" : "ostvareno"} <span className="text-[#92400E]">({m.reason})</span>
                  </p>
                ))}
                <p className="text-[11px] text-[#64748B] pl-6">Provjerite prije spremanja — spremanje nije blokirano.</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{form.type === "naplata" ? "Kupac / platitelj" : "Primatelj / dobavljač"}</Label>
                <Input data-testid="invoice-counterparty" value={form.counterparty} onChange={(e) => set("counterparty", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Na što se odnosi (opis)</Label>
                <Input data-testid="invoice-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="npr. Dugoročni najam vozila" />
              </div>
              <div className="space-y-1.5">
                <Label>Iznos (ukupno s PDV-om)</Label>
                <Input data-testid="invoice-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" />
              </div>
              <div className="space-y-1.5">
                <Label>Valuta</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="invoice-currency"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="BAM">BAM</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 flex flex-col">
                <Label>Dospijeće</Label>
                <DatePickerHR data-testid="invoice-due-date" value={form.due_date} onChange={(v) => set("due_date", v)} className="w-full" />
              </div>
              <div className="space-y-1.5">
                <Label>Tip</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="invoice-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="placanje">Plaćanje (odljev)</SelectItem><SelectItem value="naplata">Naplata (priljev)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{form.direction === "ulazni" ? "Naša banka (s koje plaćamo)" : "Banka (na koju se uplaćuje)"}</Label>
                <Select value={form.bank_id} onValueChange={(v) => set("bank_id", v)}>
                  <SelectTrigger data-testid="invoice-bank"><SelectValue placeholder="Odaberi banku" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.short || b.name}</SelectItem>)}</SelectContent>
                </Select>
                {form.direction === "ulazni" && form.bank_name && (
                  <p className="text-[11px] text-[#64748B]" data-testid="invoice-recipient-bank-hint">Dobavljač prima uplatu na: {form.bank_name}{form.account_number ? ` (${form.account_number})` : ""} — sprema se kao banka primatelja.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Kategorija</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="invoice-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Broj računa</Label>
                <Input data-testid="invoice-number" value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} placeholder="npr. 123/2026" />
              </div>
              {vehicles.length > 0 && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Vozilo (opcionalno)</Label>
                  <Select value={form.vehicle || "__none__"} onValueChange={(v) => set("vehicle", v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="invoice-vehicle-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="__none__">Bez vozila</SelectItem>
                      {vehicles.map((v) => <SelectItem key={v.id} value={v.plate}>{v.plate}{v.label ? ` — ${v.label}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {form.detected_plate && !form.vehicle && (
                    <p className="text-[11px] text-[#D97706]" data-testid="invoice-plate-hint">AI je na računu pročitao registraciju "{form.detected_plate}", ali nije pronašao to vozilo u listi.</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F1F5F9]">
              <Button data-testid="invoice-save-planned" onClick={() => saveTx("planirano")} disabled={saving} className="bg-[#D97706] hover:bg-[#A87526] rounded-sm">
                <CalendarClock className="w-4 h-4 mr-2" /> Spremi kao planirano
              </Button>
              <Button data-testid="invoice-save-realized" onClick={() => saveTx("ostvareno")} disabled={saving} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Spremi kao ostvareno
              </Button>
              <Button data-testid="invoice-save-recurring" onClick={saveRecurring} disabled={saving} variant="outline" className="rounded-sm border-[#CBD5E1]">
                <Repeat className="w-4 h-4 mr-2" /> Spremi kao fiksnu stavku
              </Button>
            </div>
            <p className="text-xs text-[#64748B]">Planirano = čeka plaćanje, ulazi u projekcije i uparuje se s izvodom. Ostvareno = već plaćeno/naplaćeno. Fiksna stavka = ponavlja se svaki mjesec od mjeseca dospijeća. Nakon spremanja automatski se otvara sljedeći račun iz reda.</p>
          </div>
          <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-sm p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Pregled računa</p>
              {(pdfUrl || preview) && (
                <div className="flex gap-1">
                  <button
                    data-testid="invoice-preview-print"
                    title="Otvori u novoj kartici — ispis i preuzimanje"
                    onClick={() => openExternal(pdfUrl || preview)}
                    className="p-1.5 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="invoice-preview-expand"
                    title="Proširi pregled preko cijelog ekrana"
                    onClick={() => setExpandOpen(true)}
                    className="p-1.5 text-[#64748B] hover:text-[#0D9488] transition-colors duration-200"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {pdfUrl ? (
              <object data={pdfUrl} type="application/pdf" data-testid="invoice-preview-pdf" aria-label="Pregled PDF računa" className="w-full rounded-sm border border-[#F1F5F9] aspect-[210/297]">
                {preview ? (
                  <img src={preview} alt="Račun" className="w-full object-contain" />
                ) : (
                  <p className="text-sm text-[#64748B] p-4">PDF pregled nije podržan u ovom pregledniku — koristite ikonu pisača za otvaranje u novoj kartici.</p>
                )}
              </object>
            ) : preview ? (
              <img src={preview} alt="Račun" data-testid="invoice-preview-img" onClick={() => setExpandOpen(true)} className="w-full rounded-sm border border-[#F1F5F9] object-contain cursor-zoom-in" />
            ) : (
              <p className="text-sm text-[#64748B]">Pregled nije dostupan — provjerite polja lijevo.</p>
            )}
            {(pdfUrl || preview) && (
              <p className="text-[11px] text-[#64748B] mt-2">
                {pdfUrl ? "PDF pregled ima ugrađene kontrole: povećanje, ispis i preuzimanje." : "Klik na sliku proširuje pregled."} Ikona <Maximize2 className="w-3 h-3 inline" /> otvara pregled preko cijelog ekrana.
              </p>
            )}
          </div>
        </div>
      )}

      <Dialog open={expandOpen} onOpenChange={setExpandOpen}>
        <DialogContent data-testid="invoice-preview-dialog" className="rounded-sm p-4 flex flex-col gap-3 !max-w-[96vw] w-[1100px] h-[94vh]">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-heading flex items-center justify-between gap-3 pr-8">
              <span className="truncate">{activeItem?.name || "Pregled računa"}</span>
              {(pdfUrl || preview) && (
                <Button size="sm" variant="outline" onClick={() => openExternal(pdfUrl || preview)} className="rounded-sm border-[#CBD5E1] h-8 shrink-0" data-testid="invoice-preview-dialog-print">
                  <Printer className="w-3.5 h-3.5 mr-1.5" /> Ispis / preuzmi
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {pdfUrl ? (
            <object data={pdfUrl} type="application/pdf" aria-label="Pregled PDF računa" className="w-full flex-1 rounded-sm border border-[#F1F5F9]">
              {preview && (
                <div className="h-full overflow-auto"><img src={preview} alt="Račun" className="w-full object-contain" /></div>
              )}
            </object>
          ) : preview ? (
            <div className="flex-1 overflow-auto">
              <img src={preview} alt="Račun" className="w-full object-contain" />
            </div>
          ) : (
            <p className="text-sm text-[#64748B]">Pregled nije dostupan.</p>
          )}
        </DialogContent>
      </Dialog>

      {!form && queue.length === 0 && (
        <div className="bg-white border border-dashed border-[#CBD5E1] rounded-sm p-10 text-center text-sm text-[#64748B]" data-testid="invoice-empty-state">
          <ReceiptText className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          Slikajte ili učitajte jedan ili više računa — AI će ih pročitati redom, a vi ih samo provjerite i spremite.
        </div>
      )}

      {!form && queue.length > 0 && !parsing && pendingReview === 0 && (
        <div className="bg-[#2F7D4F]/5 border border-[#2F7D4F]/30 rounded-sm p-6 text-center text-sm text-[#2F7D4F] font-medium" data-testid="invoice-all-done">
          <CheckCircle2 className="w-6 h-6 mx-auto mb-2" /> Svi računi iz reda su obrađeni.
        </div>
      )}
    </div>
  );
}
